import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Breadcrumb, Button, Input, Segment } from 'semantic-ui-react';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import SidebarPopup from '@plone/volto/components/manage/Sidebar/SidebarPopup';
import backSVG from '@plone/volto/icons/back.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import flashSVG from '@plone/volto/icons/flash.svg';
import folderSVG from '@plone/volto/icons/folder.svg';
import homeSVG from '@plone/volto/icons/home.svg';
import rightArrowSVG from '@plone/volto/icons/right-key.svg';
import searchSVG from '@plone/volto/icons/zoom.svg';

import type { Skill, SkillGroup } from '../../types';

// Volto types the popup from its PropTypes, which do not mention children.
const Popup = SidebarPopup as unknown as React.ComponentType<{
  open: boolean;
  onClose: () => void;
  overlay?: boolean;
  children: React.ReactNode;
}>;

const messages = defineMessages({
  chooseSkill: {
    id: 'assistantChooseSkill',
    defaultMessage: 'Choose action',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  searchSkills: {
    id: 'assistantSearchSkills',
    defaultMessage: 'Search actions',
  },
  searchResults: {
    id: 'Search results',
    defaultMessage: 'Search results',
  },
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  close: {
    id: 'Close',
    defaultMessage: 'Close',
  },
  browse: {
    id: 'Browse',
    defaultMessage: 'Browse',
  },
  select: {
    id: 'Select',
    defaultMessage: 'Select',
  },
  noSkills: {
    id: 'assistantNoSkills',
    defaultMessage: 'This assistant offers no actions.',
  },
  emptyGroup: {
    id: 'assistantEmptySkillGroup',
    defaultMessage: 'This group holds no actions.',
  },
  noMatches: {
    id: 'assistantNoMatchingSkills',
    defaultMessage: 'No action matches that.',
  },
});

export const matchingSkills = (skills: Skill[], query: string): Skill[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return skills;

  return skills.filter((skill) =>
    [skill.title, skill.description, skill.name].some((text) =>
      text.toLowerCase().includes(needle),
    ),
  );
};

type Entry =
  | { kind: 'group'; group: SkillGroup }
  | { kind: 'skill'; skill: Skill };

/**
 * What the top level shows: the groups as folders and the skills that belong
 * to none, in one order by position. A skill and a group at the same
 * position put the skill first, as the agent's own listing does.
 */
export const topLevelEntries = (
  skills: Skill[],
  groups: SkillGroup[],
): Entry[] => {
  const entries: Entry[] = [
    ...groups.map((group): Entry => ({ kind: 'group', group })),
    ...skills
      .filter((skill) => skill.group === null)
      .map((skill): Entry => ({ kind: 'skill', skill })),
  ];
  const key = (entry: Entry): [number, number, string] =>
    entry.kind === 'group'
      ? [entry.group.position, 1, entry.group.name]
      : [entry.skill.position, 0, entry.skill.name];

  return entries.sort((a, b) => {
    const [pa, ka, na] = key(a);
    const [pb, kb, nb] = key(b);

    return pa - pb || ka - kb || na.localeCompare(nb);
  });
};

export const groupEntries = (skills: Skill[], group: string): Entry[] =>
  skills
    .filter((skill) => skill.group === group)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
    .map((skill): Entry => ({ kind: 'skill', skill }));

interface SkillPickerProps {
  skills: Skill[];
  groups: SkillGroup[];
  open: boolean;
  onClose: () => void;
  onPick: (skill: Skill) => void;
}

/**
 * The skills the agent offers, shown to editors as actions, browsed the way
 * site content is picked: a sidebar that slides in over the panel, groups
 * as folders, a breadcrumb back to the top, and a search across all of them.
 * Picking one hands it to the composer, which shows it as a chip, and closes
 * the sidebar; the message is not sent.
 *
 * It borrows the object browser's markup and class names on purpose, so
 * the theme that styles one styles the other.
 */
const SkillPicker = ({
  skills,
  groups,
  open,
  onClose,
  onPick,
}: SkillPickerProps) => {
  const intl = useIntl();
  const [current, setCurrent] = React.useState<SkillGroup | null>(null);
  const [searching, setSearching] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<Input>(null);

  // Every opening starts at the top, not where the last one left off.
  React.useEffect(() => {
    if (open) return;
    setCurrent(null);
    setSearching(false);
    setQuery('');
  }, [open]);

  React.useEffect(() => {
    if (searching) searchRef.current?.focus();
  }, [searching]);

  const pick = (skill: Skill) => {
    onPick(skill);
    onClose();
  };

  const entries: Entry[] = searching
    ? matchingSkills(skills, query).map((skill) => ({ kind: 'skill', skill }))
    : current
      ? groupEntries(skills, current.name)
      : topLevelEntries(skills, groups);

  const emptyMessage = searching
    ? messages.noMatches
    : current
      ? messages.emptyGroup
      : messages.noSkills;

  return (
    <Popup open={open} onClose={onClose} overlay>
      <Segment.Group raised className="object-browser assistant-skill-picker">
        <header className="header pulled">
          <div className="vertical divider" />
          {searching ? (
            <Input
              className="search"
              ref={searchRef}
              value={query}
              placeholder={intl.formatMessage(messages.searchSkills)}
              onChange={(event) => setQuery(event.target.value)}
            />
          ) : (
            <>
              {current ? (
                <button
                  type="button"
                  aria-label={intl.formatMessage(messages.back)}
                  onClick={() => setCurrent(null)}
                >
                  <Icon name={backSVG} size="24px" />
                </button>
              ) : (
                <Icon name={flashSVG} size="24px" />
              )}
              <h2>{intl.formatMessage(messages.chooseSkill)}</h2>
            </>
          )}
          <button
            type="button"
            aria-label={intl.formatMessage(messages.search)}
            aria-pressed={searching}
            onClick={() => {
              setSearching((on) => !on);
              setQuery('');
            }}
          >
            <Icon name={searchSVG} size="24px" />
          </button>
          <button
            type="button"
            className="clearSVG"
            aria-label={intl.formatMessage(messages.close)}
            onClick={onClose}
          >
            <Icon name={clearSVG} size="24px" />
          </button>
        </header>
        <Segment secondary className="breadcrumbs" vertical>
          {searching ? (
            <div className="searchResults">
              {intl.formatMessage(messages.searchResults)}
            </div>
          ) : (
            <Breadcrumb>
              <Breadcrumb.Section
                role="button"
                aria-label={intl.formatMessage(messages.home)}
                onClick={() => setCurrent(null)}
              >
                <Icon
                  className="home-icon"
                  name={homeSVG}
                  size="18px"
                  title={intl.formatMessage(messages.home)}
                />
              </Breadcrumb.Section>
              {current && (
                <>
                  <Breadcrumb.Divider />
                  <Breadcrumb.Section active>
                    {current.title}
                  </Breadcrumb.Section>
                </>
              )}
            </Breadcrumb>
          )}
        </Segment>
        <Segment as="ul" className="object-listing">
          {entries.length === 0 && (
            <li className="assistant-skill-picker-empty">
              {intl.formatMessage(emptyMessage)}
            </li>
          )}
          {entries.map((entry) =>
            entry.kind === 'group' ? (
              <li
                key={`group:${entry.group.name}`}
                role="presentation"
                aria-label={`${intl.formatMessage(messages.browse)} ${
                  entry.group.title
                }`}
                onClick={() => setCurrent(entry.group)}
              >
                <span>
                  <Icon name={folderSVG} size="24px" />
                  {entry.group.title}
                </span>
                <div role="presentation" className="right-arrow-link-mode">
                  <Button.Group>
                    <Button
                      type="button"
                      basic
                      icon
                      aria-label={`${intl.formatMessage(messages.browse)} ${
                        entry.group.title
                      }`}
                    >
                      <Icon
                        className="right-arrow-icon"
                        name={rightArrowSVG}
                        size="24px"
                      />
                    </Button>
                  </Button.Group>
                </div>
              </li>
            ) : (
              <li
                key={`skill:${entry.skill.name}`}
                role="presentation"
                aria-label={`${intl.formatMessage(messages.select)} ${
                  entry.skill.title
                }`}
                onClick={() => pick(entry.skill)}
              >
                <span>
                  <Icon name={flashSVG} size="24px" />
                  <span className="assistant-skill-picker-text">
                    <span className="assistant-skill-picker-title">
                      {entry.skill.title}
                    </span>
                    <span className="assistant-skill-picker-description">
                      {entry.skill.description}
                    </span>
                  </span>
                </span>
              </li>
            ),
          )}
        </Segment>
      </Segment.Group>
    </Popup>
  );
};

export default SkillPicker;
